import { ArrowBack, LockOutlined, SettingsOutlined } from '@mui/icons-material';
import { Button, CardContainer } from 'finara-components';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { LoadingState } from '@/components/common/LoadingState';
import { paymentOtpService } from '@/services/paymentOtp.service';
import {
  PaymentWizardState,
  PaymentStep,
  PaymentType,
  PaymentSource,
  PaymentBeneficiary,
  PaymentData as PaymentDataType,
  PaymentSummary,
  PaymentRequest,
  BulkPaymentValidation,
  BulkPaymentResult,
} from '@/types/payment';

import { BulkPreview } from './steps/BulkPreview';
import { BulkProcessing } from './steps/BulkProcessing';
import { BulkResults } from './steps/BulkResults';
import { BulkTemplate } from './steps/BulkTemplate';
import { BulkUpload } from './steps/BulkUpload';
import { FundSource } from './steps/FundSource';
import { OTPVerification } from './steps/OTPVerification';
import { PaymentData } from './steps/PaymentData';
import { PaymentSuccess } from './steps/PaymentSuccess';
import { ReviewPayment } from './steps/ReviewPayment';
import { TypeSelection } from './steps/TypeSelection';

interface PaymentWizardProps {
  onClose: () => void;
  /**
   * When provided (e.g. a "Depositar" deposit), the wizard seeds the beneficiary
   * and starts the single-transfer path at the fund-source step.
   */
  initialBeneficiary?: PaymentBeneficiary;
}

const getStepProgress = (step: PaymentStep): number => {
  const progressMap: Record<PaymentStep, number> = {
    'type-selection': 0,
    'fund-source': 17,
    'payment-data': 40,
    'bulk-template': 17,
    'bulk-upload': 33,
    'bulk-preview': 67,
    review: 60,
    'otp-verification': 80,
    'bulk-processing': 83,
    success: 100,
    'bulk-results': 100,
  };
  return progressMap[step];
};

export const PaymentWizard: React.FC<PaymentWizardProps> = ({
  onClose,
  initialBeneficiary,
}) => {
  const navigate = useNavigate();
  const [hasMfa, setHasMfa] = useState<boolean | null>(null);
  const [isCheckingMfa, setIsCheckingMfa] = useState(true);

  const [wizardState, setWizardState] = useState<PaymentWizardState>(
    initialBeneficiary
      ? {
          // Deposit: prefill the beneficiary and start at fund-source as a
          // single transfer (type-selection is skipped).
          currentStep: 'fund-source',
          progress: getStepProgress('fund-source'),
          paymentType: 'singular',
          data: {
            beneficiary: initialBeneficiary,
            amount: 0,
            concept: '',
            executionType: 'immediate',
            scheduledDate: '',
            scheduledTime: '',
          },
        }
      : {
          currentStep: 'type-selection',
          progress: 0,
        }
  );

  // Check if user has MFA enabled on mount
  useEffect(() => {
    const checkMfa = async () => {
      try {
        const mfaEnabled = await paymentOtpService.hasMfaEnabled();
        setHasMfa(mfaEnabled);
      } catch (error) {
        console.error('Error al verificar el estado de MFA:', error);
        setHasMfa(false);
      } finally {
        setIsCheckingMfa(false);
      }
    };

    void checkMfa();
  }, []);

  const updateWizardState = (updates: Partial<PaymentWizardState>) => {
    setWizardState(prev => ({
      ...prev,
      ...updates,
      progress: getStepProgress(updates.currentStep || prev.currentStep),
    }));
  };

  const goToStep = (step: PaymentStep) => {
    updateWizardState({ currentStep: step });
  };

  const goBack = () => {
    const { currentStep, paymentType } = wizardState;

    // Define step order based on payment type
    const singularSteps: PaymentStep[] = [
      'type-selection',
      'fund-source',
      'payment-data',
      'review',
      'otp-verification',
      'success',
    ];

    const bulkSteps: PaymentStep[] = [
      'type-selection',
      'fund-source',
      'bulk-template',
      'bulk-upload',
      'bulk-preview',
      'otp-verification',
      'bulk-processing',
      'bulk-results',
    ];

    const stepOrder = paymentType === 'bulk' ? bulkSteps : singularSteps;
    const currentIndex = stepOrder.indexOf(currentStep);

    if (currentIndex > 0) {
      goToStep(stepOrder[currentIndex - 1] as PaymentStep);
    } else {
      onClose();
    }
  };

  const renderStep = () => {
    switch (wizardState.currentStep) {
      case 'type-selection':
        return (
          <TypeSelection
            onSelect={(paymentType: PaymentType) => {
              updateWizardState({
                paymentType,
                currentStep: 'fund-source',
              });
            }}
          />
        );

      case 'fund-source':
        return (
          <FundSource
            onSelect={(source: PaymentSource) => {
              const nextStep =
                wizardState.paymentType === 'bulk' ? 'bulk-template' : 'payment-data';
              updateWizardState({
                source,
                currentStep: nextStep,
              });
            }}
          />
        );

      case 'payment-data':
        return (
          <PaymentData
            initialData={wizardState.data}
            onSubmit={(data: PaymentDataType, summary: PaymentSummary) => {
              updateWizardState({
                data,
                summary,
                currentStep: 'review',
              });
            }}
          />
        );

      case 'bulk-template':
        return (
          <BulkTemplate
            onNext={() => {
              updateWizardState({
                currentStep: 'bulk-upload',
              });
            }}
          />
        );

      case 'bulk-upload':
        return (
          <BulkUpload
            onFileProcessed={(validation: BulkPaymentValidation) => {
              updateWizardState({
                bulkValidation: validation,
                currentStep: 'bulk-preview',
              });
            }}
          />
        );

      case 'bulk-preview':
        return (
          wizardState.bulkValidation && (
            <BulkPreview
              validation={wizardState.bulkValidation}
              onUpdateValidation={(validation: BulkPaymentValidation) => {
                updateWizardState({
                  bulkValidation: validation,
                });
              }}
              onNext={() => {
                updateWizardState({
                  currentStep: 'otp-verification',
                });
              }}
            />
          )
        );

      case 'bulk-processing':
        return (
          wizardState.bulkValidation && (
            <BulkProcessing
              validation={wizardState.bulkValidation}
              backendResponse={wizardState.bulkResponse}
              onProcessingComplete={(results: BulkPaymentResult) => {
                updateWizardState({
                  bulkResults: results,
                  currentStep: 'bulk-results',
                });
              }}
            />
          )
        );

      case 'bulk-results':
        return (
          wizardState.bulkResults && (
            <BulkResults
              results={wizardState.bulkResults}
              onFinish={() => {
                onClose();
              }}
            />
          )
        );

      case 'review':
        return (
          <ReviewPayment
            wizardState={wizardState}
            onConfirm={() => {
              updateWizardState({
                currentStep: 'otp-verification',
              });
            }}
          />
        );

      case 'otp-verification':
        return (
          <OTPVerification
            wizardState={wizardState}
            onVerify={(paymentRequest: PaymentRequest) => {
              const nextStep = wizardState.paymentType === 'bulk' ? 'bulk-processing' : 'success';
              updateWizardState({
                paymentRequest,
                currentStep: nextStep,
              });
            }}
          />
        );

      case 'success':
        return (
          <PaymentSuccess
            paymentRequest={wizardState.paymentRequest!}
            onNewPayment={() => {
              setWizardState({
                currentStep: 'type-selection',
                progress: 0,
              });
            }}
            onViewOperations={() => {
              // Navigate to operations page
              onClose();
            }}
          />
        );

      default:
        return null;
    }
  };

  const showBackButton = !['success', 'bulk-results', 'bulk-processing'].includes(
    wizardState.currentStep
  );
  const backButtonText =
    wizardState.currentStep === 'type-selection' ? 'Volver a Dashboard' : 'Regresar';

  // Show loading while checking MFA
  if (isCheckingMfa) {
    return (
      <div className="min-h-screen">
        <div className="bg-surface border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button
                variant="white"
                startIcon={<ArrowBack />}
                onClick={onClose}
                className="text-muted"
              >
                Volver a Dashboard
              </Button>
              <div className="w-24" />
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="" size="large" fullHeight={true} className="py-12" />
        </div>
      </div>
    );
  }

  // Show MFA required message if user doesn't have MFA enabled
  if (hasMfa === false) {
    return (
      <div className="min-h-screen">
        <div className="bg-surface border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Button
                variant="white"
                startIcon={<ArrowBack />}
                onClick={onClose}
                className="text-muted"
              >
                Volver a Dashboard
              </Button>
              <div className="w-24" />
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="max-w-2xl mx-auto">
            <CardContainer className="p-8">
              <div className="text-center">
                {/* Icon */}
                <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <LockOutlined className="text-orange-600" fontSize="large" />
                </div>

                {/* Title */}
                <h2 className="text-2xl font-bold text-brand-white mb-4">
                  Autenticación de Dos Factores Requerida
                </h2>

                {/* Message */}
                <p className="text-muted mb-6">
                  Para procesar pagos, es necesario configurar la autenticación de dos factores
                  (2FA). Esta medida de seguridad protege tus transacciones y garantiza que solo tú
                  puedas autorizar pagos.
                </p>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="black"
                    size="large"
                    startIcon={<SettingsOutlined />}
                    onClick={() => void navigate('/configuracion')}
                  >
                    Configurar 2FA Ahora
                  </Button>
                  <Button variant="white" size="large" onClick={onClose}>
                    Volver al Dashboard
                  </Button>
                </div>
              </div>
            </CardContainer>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <div className="bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {showBackButton && (
              <Button
                variant="white"
                startIcon={<ArrowBack />}
                onClick={goBack}
                className="text-muted"
              >
                {backButtonText}
              </Button>
            )}
            <div className="w-24" /> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      {wizardState.currentStep !== 'success' && wizardState.progress > 0 && (
        <div className="bg-surface border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-brand-white">Progreso del Pago</span>
              <span className="text-sm text-muted">{wizardState.progress}%</span>
            </div>
            <div className="w-full bg-black/10 dark:bg-white/10 rounded-full h-2">
              <div
                className="bg-brand-blue h-2 rounded-full transition-all duration-300"
                style={{ width: `${wizardState.progress}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{renderStep()}</div>
    </div>
  );
};
